import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'

// ── Plan catalogue ─────────────────────────────────────────────────────────────
// Since Phase 4 the catalogue lives in the `plans` table (editable from
// HQ → Settings without a deploy). The values below are the startup fallback;
// refreshPlans() swaps in the DB rows IN PLACE so every module that imported
// PLANS — including pure helpers like orgMrr() — sees the fresh values.
export const PLANS = [
  {
    tier: 'starter',
    name: 'Starter',
    description: '1 branch · 5 users · basic analytics',
    plan_code: 'PLN_sytcszm707m99nb',
    amount: 50_000,
    maxBranches: 1,
    maxUsers: 5,
  },
  {
    tier: 'growth',
    name: 'Growth',
    description: '5 branches · 20 users · all features',
    plan_code: 'PLN_hzcjwwmel75caw8',
    amount: 220_000,
    maxBranches: 5,
    maxUsers: 20,
  },
  {
    tier: 'enterprise',
    name: 'Enterprise',
    description: 'Unlimited branches · unlimited users · priority support',
    plan_code: 'PLN_jprcqn6lmbjcn80',
    amount: 300_000,
    maxBranches: null, // unlimited
    maxUsers: null,
  },
]

function mapPlanRow(r) {
  return {
    tier: r.tier,
    name: r.name,
    description: r.description,
    plan_code: r.paystack_plan_code,
    amount: Number(r.amount),
    maxBranches: r.max_branches,
    maxUsers: r.max_users,
  }
}

export async function refreshPlans() {
  const { data } = await supabase.from('plans').select('*').order('sort_order')
  if (data && data.length > 0) {
    PLANS.length = 0
    PLANS.push(...data.map(mapPlanRow))
  }
  return PLANS
}

// Load the DB catalogue once per mount tree and re-render with it.
export function usePlans() {
  const [plans, setPlans] = useState([...PLANS])
  useEffect(() => {
    let alive = true
    refreshPlans().then((p) => { if (alive) setPlans([...p]) })
    return () => { alive = false }
  }, [])
  return plans
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useBilling(currentUser) {
  usePlans() // keep the catalogue fresh for consumers rendering PLANS
  const [billing, setBilling] = useState(null) // { subscription_status, current_period_end, plan }
  const [loading, setLoading] = useState(true)

  const fetchBilling = useCallback(async () => {
    if (!currentUser?.organizationId) { setLoading(false); return }
    const { data } = await supabase
      .from('organizations')
      .select('subscription_status, current_period_end, plan')
      .eq('id', currentUser.organizationId)
      .single()
    setBilling(data)
    setLoading(false)
  }, [currentUser?.organizationId])

  useEffect(() => { fetchBilling() }, [fetchBilling])

  // Open Paystack popup for a given plan_code.
  // On success the webhook will update the DB; we poll for the change.
  function initializePayment({ plan_code, email, amount }) {
    return new Promise((resolve) => {
      const handler = window.PaystackPop.setup({
        key: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY,
        email,
        amount: amount * 100, // kobo — Paystack subscriptions ignore amount (plan drives it)
        plan: plan_code,
        currency: 'NGN',
        ref: `PT-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        label: 'PetroTrack Subscription',
        metadata: { organization_id: currentUser.organizationId },
        onSuccess(transaction) {
          resolve({ success: true, reference: transaction.reference })
        },
        onCancel() {
          resolve({ success: false })
        },
      })
      handler.openIframe()
    })
  }

  async function cancelSubscription() {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await supabase.functions.invoke('paystack-cancel-subscription', {
      headers: { Authorization: `Bearer ${session?.access_token}` },
    })
    if (res.error || res.data?.error) {
      return { success: false, message: res.data?.error ?? res.error?.message ?? 'Failed to cancel.' }
    }
    return { success: true }
  }

  return { billing, loading, refetch: fetchBilling, initializePayment, cancelSubscription }
}
